import { persistentAtom } from "@nanostores/persistent";
import * as Sentry from "@sentry/nextjs";
import { user } from "common/stores/UserStore";
import {
	ESPStored,
	FormMetadata,
	ReplyAssociation,
	ReplyContent,
	ReplyContentIdentifier,
	SurveyAnswers,
	SurveyResponse,
} from "common/types/Survey";
import { transformAnswersForApi } from "common/utils/FormUtils";
import { IUser } from "common/utils/UserUtils";
import isEmpty from "lodash-es/isEmpty";
import InquireClient from "common/clients/InquireClient";
import { atom, computed, map } from "nanostores";
import { msg } from "./SnackbarStore";

export const activeReplyId = persistentAtom("activeReplyId", null, {
	encode: JSON.stringify,
	decode: JSON.parse,
});
export const dashboardCampaignId = atom<string | number>(0);
export const dashboardCampaignIds = atom<number[] | undefined>(undefined);
export const currentCampaignTitle = persistentAtom<string | null>(
	"campaignTitle",
	null,
	{
		encode: JSON.stringify,
		decode: JSON.parse,
	}
);
export const replyIds = atom<number[]>([]);
export const hasMultipleCampaigns = computed(replyIds, (ids) => {
	return ids.length > 1;
});
export const espStats = atom<ESPStored>({
	campaign_id: 0,
	total_new: 0,
	total_existing: 0,
	failure_count: 0,
	errors: [],
});

export const replyAssociation = atom<ReplyAssociation>({
	campaign_id: 0,
	id: 0,
	reply_content_ids: [],
	reply_id: 0,
	submitted_at: "",
	surveyee_email_address: "",
	surveyee_id: "",
	surveyee_name: "",
	surveyor_id: "",
	surveyor_name: "",
	third_party_metadata: {},
	status: "",
	is_active: true,
});

export const surveyReplyContents = atom<Array<ReplyContent>>([]);

export const surveyors = atom<Array<string>>([]);

export const surveyInfo = map<SurveyResponse>({
	campaign_name: "",
	orchestrator_name: "",
	campaign_status: "",
	esp_list_id: "",
	id: 0,
	orchestrator_id: 0,
	created_at: "",
	modified_at: "",
	campaign_metadata: null,
});

export const surveyYear = computed(surveyInfo, (info) => {
	return new Date(info?.campaign_metadata?.start_time).getFullYear() - 1;
});

const coreFinder = (item: ReplyContent) =>
	item.content_identifier &&
	["consumption_estimate", "emissions_estimate", "general_emissions"].includes(
		item.content_identifier
	);

export const coreSurveyAnswers = computed(
	surveyReplyContents,
	(replyContents) => {
		const coreReply = replyContents.find(coreFinder);
		// The `content_json` appears to be directly passed to some component props.
		// It seems to typically contain form data, and the backend returns it as-is.
		// We should consider modeling this data more effectively to ensure better structure and validation.
		return (coreReply?.content_json || {
			hasReportableData: "",
		}) as SurveyAnswers;
	}
);

export const formMetadata = computed(surveyReplyContents, (replyContents) => {
	if (replyContents.length == 0) {
		return {};
	}
	const coreReply = replyContents.find(coreFinder);
	return (coreReply?.form_metadata || {}) as FormMetadata;
});

export const contentIdentifier = computed(
	surveyReplyContents,
	(replyContents) => {
		const coreReply = replyContents.find(coreFinder);
		return coreReply?.content_identifier;
	}
);

export const submitStatus = computed(
	[surveyReplyContents, replyAssociation],
	(replyContents, replyAssoc) => {
		const coreReply = replyContents.find(coreFinder);
		return coreReply?.modified_at && replyAssoc.submitted_at
			? coreReply?.modified_at > replyAssoc.submitted_at
			: false;
	}
);

export const userSurveySetup = (user: IUser) => {
	if (isEmpty(user.traits)) return;

	if (user.traits?.["survey:surveyor"]) {
		dashboardCampaignIds.set(
			user.traits["survey:surveyor"].map((trait) => Number(trait.campaign_id))
		);
	}

	if (user.traits?.["survey:surveyor"]?.[0]) {
		dashboardCampaignId.set(
			Number(user.traits["survey:surveyor"][0].campaign_id)
		);
	}

	const surveyeeTraits = user.traits?.["survey:surveyee"];
	if (Array.isArray(surveyeeTraits)) {
		const traitsReplyIds = surveyeeTraits.map((surveyeeTrait) =>
			Number(surveyeeTrait.reply_id)
		);
		replyIds.set(traitsReplyIds);
	}
};

export async function getCampaignData(replyId: number) {
	InquireClient.getSurveyInfo(replyId)
		.then((surveyResponse) => {
			surveyInfo.set({
				...surveyResponse,
			});
			Sentry.setContext("Survey", { ...surveyResponse });
		})
		.catch((e) => {
			console.error("Could not retrieve survey end time", e);
		});
	InquireClient.getSurveyors(replyId)
		.then((surveyorsResponse) => {
			surveyors.set(surveyorsResponse);
		})
		.catch((e) => {
			console.error("Could not retrieve surveyors", e);
		});

	await InquireClient.getReplyAssociations(replyId)
		.then((replyResp) => {
			if (replyResp?.length)
				replyAssociation.set(replyResp[0] as ReplyAssociation);
		})
		.catch(() => {
			console.error("Could not retrieve reply");
		});

	await InquireClient.getContents(replyId)
		.then((contents) => {
			surveyReplyContents.set(contents.items);
		})
		.catch((e) => {
			window.location.replace("/survey/accessFail");
			console.error(
				"May have incorrect organization selected or have a bad reply id",
				replyId,
				e
			);
			msg.set({
				message: `Failed to start survey reply for ${replyId}`,
				autoHide: false,
				severity: "error",
			});
		});
}

export const setAnswers = async (
	answers: SurveyAnswers,
	contentType: ReplyContentIdentifier | undefined,
	metadata?: FormMetadata
): Promise<void> => {
	const replyId = activeReplyId.get();
	const userName = user.get().username;

	const getCurrentContent = () =>
		surveyReplyContents
			.get()
			.find(
				(element: ReplyContent) => element.content_identifier === contentType
			);

	const oldContent = getCurrentContent();

	const newContent = transformAnswersForApi(
		answers,
		contentType,
		metadata,
		replyId,
		userName,
		oldContent?.modified_at,
		oldContent?.created_by,
		oldContent?.id
	);

	const updateSurveyReplyContents = (updated: ReplyContent) => {
		const updatedSurveyContents = surveyReplyContents
			.get()
			.map((rc) => (rc.id !== updated?.id ? rc : updated));
		surveyReplyContents.set(updatedSurveyContents);
	};

	const fetchAndSetContents = async (): Promise<ReplyContent[] | null> => {
		try {
			const contents = await InquireClient.getContents(replyId);
			surveyReplyContents.set(contents.items);
			return contents.items;
		} catch (e) {
			console.error(
				"May have incorrect organization selected or have a bad reply id",
				replyId,
				e
			);
			msg.set({
				message: `Failed to start survey reply for ${replyId}`,
				autoHide: false,
				severity: "error",
			});
			window.location.replace("/survey/accessFail");
			return null;
		}
	};

	if (oldContent) {
		try {
			const replyContentResp = await InquireClient.updateContent(
				replyId,
				oldContent,
				newContent
			);
			updateSurveyReplyContents(replyContentResp);
		} catch (e) {
			if (e.response?.status === 409) {
				console.warn(
					"409 Conflict detected, refetching latest content and retrying..."
				);

				const refreshedContents = await fetchAndSetContents();
				if (!refreshedContents) return;

				const latestContent = refreshedContents.find(
					(rc) => rc.content_identifier === contentType
				);

				if (latestContent) {
					const retriedContent = transformAnswersForApi(
						answers,
						contentType,
						metadata,
						replyId,
						userName,
						latestContent.modified_at,
						latestContent.created_by,
						latestContent.id
					);

					try {
						const retryResp = await InquireClient.updateContent(
							replyId,
							latestContent,
							retriedContent
						);
						updateSurveyReplyContents(retryResp);
					} catch (retryErr) {
						console.error("Retry PUT replyContent failed", retryErr);
					}
				} else {
					console.error(
						"Retry failed: Could not find latest content after refresh."
					);
				}
			} else {
				console.error("PUT replyContent failed", e);
			}
		}
		return;
	}

	// No existing content — create new
	try {
		const newContentItem = await InquireClient.createContent(
			newContent,
			replyId
		);
		surveyReplyContents.set([...surveyReplyContents.get(), newContentItem]);
	} catch (e) {
		console.error("POST replyContent failed", e);
	}
};
